import { Calendar } from 'lucide-react'
import { useState } from 'react'
import { formatEventShortDay, formatEventTimeRange } from '~/features/events/lib/event-date-format'
import * as m from '~/paraglide/messages'
import type { Event } from '~/shared/types'

type EventOccurrence = Event['occurrences'][number]

const VISIBLE_LIMIT = 5

function formatOccurrence(occurrence: EventOccurrence): string {
  const start = new Date(occurrence.startTime)
  const day = formatEventShortDay(start)
  if (occurrence.isAllDay) return `${day} · ${m.allDay()}`
  return `${day} · ${formatEventTimeRange(start, new Date(occurrence.endTime))}`
}

export function OccurrenceList({ occurrences }: { occurrences: EventOccurrence[] }) {
  const [expanded, setExpanded] = useState(false)
  const now = Date.now()
  const sorted = [...occurrences].sort((a, b) => Date.parse(a.startTime) - Date.parse(b.startTime))
  const upcoming = sorted.filter((occurrence) => Date.parse(occurrence.endTime) >= now)
  const list = upcoming.length > 0 ? upcoming : sorted
  const visible = expanded ? list : list.slice(0, VISIBLE_LIMIT)
  const hasMore = list.length > VISIBLE_LIMIT

  return (
    <div>
      <p className="text-sm text-muted-foreground mb-1">{m.recurringDatesLabel()}</p>
      <ul className="space-y-1">
        {visible.map((occurrence) => (
          <li key={occurrence.id} className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-muted-foreground shrink-0" />
            <span className="text-sm font-medium">{formatOccurrence(occurrence)}</span>
          </li>
        ))}
      </ul>
      {hasMore && (
        <button
          type="button"
          aria-expanded={expanded}
          onClick={() => setExpanded((value) => !value)}
          className="mt-1.5 text-sm font-semibold text-brand-subtle-foreground"
        >
          {expanded ? m.showLess() : m.showAllDates({ count: list.length })}
        </button>
      )}
    </div>
  )
}
